import React from "react";
import {connect} from "react-redux";
import {Button} from "antd";
import {LeftOutlined, RightOutlined} from "@ant-design/icons";
import {GlobalHotKeys} from "react-hotkeys";
import {
    EditorType, ElementsValues, ElementsValuesType, getElementsValues, Nullable,
} from "@data-force/app";
import "./TaskChanger.scss";
import {getTranslate, TranslateFunction} from "react-localize-redux";
import {
    ChangeActiveTask, Status, StatusIconData, Task, TaskEntity,
} from "../../interfaces/store/actions/payload/task";
import {
    getHistoryParams, getStatusIconData, isTaskMetadataValid, isTaskValid,
} from "../../utils/common";
import {ChangeActiveTaskAction} from "../../interfaces/store/actions/task";
import StoreState from "../../interfaces/store/storeState";
import HistoryParams from "../../interfaces/historyParams";
import {changeActiveTask} from "../../store/actions/task";
import {GetFormErrorsPayload} from "../../interfaces/store/actions/payload/job";

interface DispatchProps {
    changeActiveTask(payload: ChangeActiveTask): ChangeActiveTaskAction
}

interface StateProps {
    tasks: TaskEntity[]
    activeTask: Nullable<Task>
    translate: TranslateFunction
    isValidTask: boolean
    activeTaskEntity: Nullable<TaskEntity>
    validTaskMetadata: boolean
}

type Props = DispatchProps & StateProps;

class TaskChanger extends React.Component<Props> {
    private static readonly translationPath: string = "components.TaskChanger.TaskChanger";

    private readonly keyMap = {
        nextTask: ["CTRL+ArrowRight", "CTRL+Right", "CTRL+Meta+ArrowRight", "CTRL+Meta+Right"],
        previousTask: ["CTRL+ArrowLeft", "CTRL+Left", "CTRL+Meta+ArrowLeft", "CTRL+Meta+Left"],
    };

    private handlers = {
        nextTask: this.navigate.bind(this, 1),
        previousTask: this.navigate.bind(this, -1),
    };

    /**
     * Change active task to next or previous
     *
     * @param {number} movement
     */
    navigate(movement: 1 | -1): void {
        const {activeTask, tasks}: Props = this.props;
        if (activeTask) {
            const {id}: Task = activeTask;
            const currentTaskIndex: number = tasks.findIndex((task: TaskEntity): boolean => id === task.payload);
            if (tasks.length > 1) {
                // Check if there is enough task to enable task change trough shortcuts
                const navigateFirstTaskToNext: boolean = currentTaskIndex === 0 && movement !== -1;
                const navigateLastTaskToPrevious: boolean = currentTaskIndex === (tasks.length - 1) && movement !== 1;
                const navigateInBetween: boolean = currentTaskIndex > 0 && currentTaskIndex < (tasks.length - 1);

                if (navigateFirstTaskToNext || navigateLastTaskToPrevious || navigateInBetween) {
                    // Don't allow change to previous task trough shortcut when first task is active and vice versa
                    this.props.changeActiveTask({id, movement});
                }
            }
        }
    }

    render(): React.ReactNode {
        const {
            tasks, activeTask, translate, isValidTask, activeTaskEntity, validTaskMetadata,
        }: Props = this.props;
        const {translationPath}: typeof TaskChanger = TaskChanger;

        const hasActiveTask: boolean = !!(tasks && activeTask?.id);
        const disablePrev: boolean = !hasActiveTask || tasks[0].payload === activeTask?.id;
        const disableNext: boolean = !hasActiveTask || tasks[tasks.length - 1].payload === activeTask?.id;
        const name: string = hasActiveTask
            ? activeTask!.name
            : translate(`${translationPath}.notAvailable.text`) as string;

        const {statusClass, statusName}: StatusIconData = getStatusIconData({
            status: activeTaskEntity?.status || Status.NOT_STARTED,
            isActiveTask: true,
            activeTaskInvalid: (activeTask?.valid === false || !validTaskMetadata),
            validTask: isValidTask,
            translationPath,
            translate,
        });

        return (
            <GlobalHotKeys keyMap={this.keyMap} handlers={this.handlers}>
                <div className="df-task-changer">
                    <Button
                        onClick={(): void => this.navigate(-1)}
                        type="link"
                        icon={<LeftOutlined className="df-navigation-icon"/>}
                        className={disablePrev ? "df-task-changer-btn-disabled" : "df-task-changer-btn-active"}
                        tabIndex={disablePrev ? -1 : 0}
                    />
                    <span className={`df-task-status-span ${statusClass}`} title={statusName}/>
                    <span className="df-task-name">
                        {name}
                    </span>
                    <Button
                        onClick={(): void => this.navigate(1)}
                        type="link"
                        icon={<RightOutlined className="df-navigation-icon"/>}
                        className={disableNext ? "df-task-changer-btn-disabled" : "df-task-changer-btn-active"}
                        tabIndex={disableNext ? -1 : 0}
                    />
                </div>
            </GlobalHotKeys>
        );
    }
}

function mapStateToProps({
    localize,
    job: {
        tasks,
        activeTask,
    },
    project,
    schema: {
        parsedSchemas,
        elementsValuesMap,
    },
}: StoreState): StateProps {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const schemaKey: string = `${project.projectDetails?.schemaId}_${historyParams?.phase}`;
    const editor: Nullable<EditorType> = parsedSchemas[schemaKey] as EditorType;
    const taskEntity: Nullable<TaskEntity> = tasks.find(
        (task: TaskEntity): boolean => task.payload === activeTask?.id,
    );
    const elementsValues: ElementsValues = taskEntity?.id
        ? getElementsValues({
            metadataId: taskEntity.id,
            elementsValuesType: ElementsValuesType.TASK,
            schemaKey,
            elementsValuesMap,
        })
        : {};
    const translate: TranslateFunction = getTranslate(localize);
    const formErrorsPayload: GetFormErrorsPayload = {
        elementsValuesMap,
        schemaKey,
        editor,
        translate,
        jobId: historyParams?.jobId,
        tasks,
    };
    const validTaskMetadata: boolean = isTaskMetadataValid(formErrorsPayload, activeTask);

    return {
        tasks,
        activeTask,
        translate: getTranslate(localize),
        isValidTask: isTaskValid(editor, elementsValues),
        activeTaskEntity: taskEntity,
        validTaskMetadata,
    };
}

const mapDispatchToProps: DispatchProps = {
    changeActiveTask,
};

export default connect(mapStateToProps, mapDispatchToProps)(TaskChanger);
